import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
export function OpeningHook() {
  return <section className="py-24 px-6 bg-navy-800" data-id="element-369">
      <div className="max-w-3xl mx-auto text-center space-y-12" data-id="element-370">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="space-y-8" data-id="element-371">
          <h2 className="text-5xl md:text-7xl font-serif text-cream" data-id="element-372">
            It's 11PM.
          </h2>

          <div className="space-y-6 text-xl md:text-2xl text-cream-300/80 font-serif leading-relaxed" data-id="element-373">
            <p data-id="element-374">You're exhausted.</p>
            <p data-id="element-375">Your eyes are heavy. Your day was long. You deserve sleep.</p>
            <p data-id="element-376">But the moment your body hits that mattress...</p>
            <p className="text-teal-400 font-bold" data-id="element-377">
              Your lower back lights up like a bonfire.
            </p>
          </div>

          <div className="space-y-4 text-lg text-cream-300/70 max-w-2xl mx-auto" data-id="element-378">
            <p data-id="element-379">
              You shift to the left. Still there. You roll to the right. Worse.
              You stack the pillows. Rearrange them. Try lying flat. Try curling
              up.
            </p>
            <p className="font-bold text-cream" data-id="element-380">Nothing works.</p>
            <p data-id="element-381">
              And somewhere between minute 20 and minute 40 of this exhausting
              position-switching ritual...
            </p>
            <p data-id="element-382">A thought creeps in that terrifies you:</p>
          </div>

          <div className="py-8" data-id="element-383">
            <p className="text-3xl md:text-4xl font-serif italic text-cream" data-id="element-384">
              "Is this just my life now?"
            </p>
          </div>

          <div className="space-y-6 text-lg text-cream-300/80" data-id="element-385">
            <p data-id="element-386">
              If you felt something reading that — if your chest tightened even
              slightly — then this letter was written for you.
            </p>
            <p data-id="element-387">Because what you just read isn't a dramatic story.</p>
            <p className="font-bold text-cream" data-id="element-388">
              It's Tuesday night for over 800 million people worldwide.
            </p>
            <p data-id="element-389">And the worst part?</p>
            <p data-id="element-390">It's not because they haven't tried.</p>
            <p className="text-2xl font-serif text-teal-400" data-id="element-391">
              They've tried everything.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
}
const failedAttempts = [{
  title: "You've tried the heating pad.",
  desc: 'It helps for 7 minutes. Then the heat fades. And the pain is still there. Waiting.'
}, {
  title: "You've tried the painkillers.",
  desc: "They take the edge off — until they don't. And now you're worried about your stomach. Your liver. Your dependency."
}, {
  title: "You've tried the YouTube stretches.",
  desc: 'Some guy in yoga pants promises relief in 5 minutes. You follow along. Feel okay during. Then wake up at 3AM worse than before.'
}, {
  title: "You've tried chiropractic adjustments.",
  desc: '$80 a session. Temporary relief. But the money runs out before the pain does.'
}, {
  title: "You've tried just ignoring it.",
  desc: 'Pushing through. Being tough about it. Until the pain started stealing things from you.'
}];
export function PainAmplification() {
  return <section className="py-24 px-6 bg-navy-900 border-y border-navy-800" data-id="element-392">
      <div className="max-w-4xl mx-auto" data-id="element-393">
        <div className="space-y-6 mb-16" data-id="element-394">
          {failedAttempts.map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="flex gap-4 items-start bg-navy-800/50 p-6 rounded-xl border border-navy-700" data-id="element-395">
              <div className="mt-1 bg-red-500/10 p-1.5 rounded-full shrink-0" data-id="element-396">
                <X className="w-5 h-5 text-red-400" data-id="element-397" />
              </div>
              <div data-id="element-398">
                <h3 className="text-xl font-bold text-cream mb-2" data-id="element-399">
                  {item.title}
                </h3>
                <p className="text-cream-300/70 leading-relaxed" data-id="element-400">{item.desc}</p>
              </div>
            </motion.div>)}
        </div>

        <div className="text-center space-y-8 mb-16" data-id="element-401">
          <p className="text-xl text-cream-300/80" data-id="element-402">
            Your sleep. Your mornings. Your patience with your kids. Your
            enjoyment of a simple walk. Your confidence that tomorrow will be
            different.
          </p>

          <p className="text-2xl font-serif text-teal-400 italic" data-id="element-403">
            Let me ask you something honest:
          </p>
        </div>

        <div className="bg-navy-800 border-l-4 border-gold p-8 md:p-12 rounded-r-2xl space-y-8 mb-16 shadow-xl" data-id="element-404">
          <p className="text-xl md:text-2xl font-serif text-cream leading-relaxed" data-id="element-405">
            How many nights have you lain awake, doing mental math on how many
            hours of sleep you might still get if you fall asleep RIGHT NOW?
          </p>
          <p className="text-xl md:text-2xl font-serif text-cream leading-relaxed" data-id="element-406">
            How many mornings have you woken up already dreading bedtime?
          </p>
          <p className="text-xl md:text-2xl font-serif text-cream leading-relaxed" data-id="element-407">
            How many times have you told yourself "I'll see a doctor about this"
            and then didn't — because deep down you're afraid of what they'll
            say?
          </p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center space-y-6" data-id="element-408">
          <h3 className="text-3xl md:text-5xl font-serif text-cream leading-tight" data-id="element-409">
            You are not weak.
            <br data-id="element-410" />
            You are not broken.
            <br data-id="element-411" />
            You are not imagining it.
          </h3>
          <p className="text-xl text-cream-300/80" data-id="element-412">
            You are simply using the wrong approach.
          </p>
          <p className="text-2xl font-bold text-teal-400" data-id="element-413">
            And tonight, that changes.
          </p>
        </motion.div>
      </div>
    </section>;
}
export function PainLoopDiagram() {
  const steps = ['Pain signal fires', "Muscles 'guard' by contracting", 'Contraction creates MORE pressure', 'Cortisol and stress hormones spike', "Nervous system enters 'threat mode'", 'Pain perception is AMPLIFIED', "You can't sleep", 'Sleep deprivation lowers pain tolerance', 'Pain signal fires louder'];
  return <section className="py-24 px-6 bg-navy-800" data-id="element-414">
      <div className="max-w-4xl mx-auto" data-id="element-415">
        <div className="text-center space-y-6 mb-16" data-id="element-416">
          <h2 className="text-3xl md:text-5xl font-serif text-cream" data-id="element-417">
            Here's what no one has told you.
          </h2>
          <p className="text-xl text-cream-300/80 max-w-2xl mx-auto" data-id="element-418">
            And it's the most important thing you will read about your back
            pain:
          </p>
          <p className="text-2xl font-bold text-teal-400" data-id="element-419">
            Your problem is not in your back.
          </p>
          <p className="text-lg text-cream-300/70" data-id="element-420">At least — not entirely.</p>
          <p className="text-lg text-cream-300/80 max-w-2xl mx-auto" data-id="element-421">
            Yes, there's inflammation. Yes, there's compression. Yes, your
            muscles are tight and your discs are angry. But the reason nothing
            has provided lasting relief is this:
          </p>
          <p className="text-3xl font-serif text-cream italic" data-id="element-422">
            Your nervous system is stuck in a pain loop.
          </p>
        </div>

        <div className="space-y-6 text-lg text-cream-300/80 max-w-3xl mx-auto text-center" data-id="element-423">
          <p data-id="element-424">This is why it gets worse at night.</p>
          <p data-id="element-425">
            During the day, movement, distraction, and cortisol naturally
            suppress the pain signal.
          </p>
          <p className="text-xl text-cream font-medium" data-id="element-426">
            But the moment you lie down in silence and stillness?
          </p>
          <p data-id="element-427">
            Your nervous system has your full attention. And it uses every
            second of it.
          </p>
          <p data-id="element-428">
            Every solution you've tried has attacked ONE point in this loop.
          </p>
          <div className="bg-navy-900 p-6 rounded-xl border border-navy-700 my-8" data-id="element-429">
            <p className="font-mono text-sm text-cream-300/60" data-id="element-430">
              Painkillers: mute the signal.
              <br data-id="element-431" />
              Stretching: target the muscle.
              <br data-id="element-432" />
              Heat pads: address the inflammation.
            </p>
          </div>
          <p className="text-xl font-bold text-cream" data-id="element-433">
            But the loop has four entry points.
          </p>
          <p data-id="element-434">And unless you interrupt ALL of them simultaneously?</p>
          <p className="text-2xl font-serif text-teal-400" data-id="element-435">
            The loop restarts. Every. Single. Night.
          </p>
        </div>
      </div>
    </section>;
}